import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { Button, Row, FormCheck, FormControl } from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { DateTimePicker } from '../../../components';
import {
  ExaminedResultEnum,
  PHIBlood,
  PHIParasite,
  PHIPhysical,
  PHIUrine,
  PHIXRay,
  UserInfo,
} from '../../../model';
import apis from '../../../apis';
import {
  downloadBlob,
  downloadSheets,
  generateSheet,
  getGradeMinMax,
} from '../../../utils';
import { DateTime } from 'luxon';
import { WrapClassBaseLayout, ClassSem } from '../../../layouts';
import { toast } from 'react-toastify';
import { I18N } from '../../../i18n-raw';
import { color } from 'html2canvas/dist/types/css/types/color';
import { CellStyle } from 'xlsx-js-style';

const mapStates = (state: ApplicationState, ownProps: ClassSem) => ({
  ...ownProps,
  ...state.auth,
});
const mapDispatch = ErrorDispatches;

const connector = connect(mapStates, mapDispatch);
type Props = ConnectedProps<typeof connector>;

enum CheckNoteSelType {
  AGREE = '同意書',
  NOTICE = '通知單',
  CHECK_LIST = '檢查結果清單',
}

const checkNoteSel: FunctionComponent<Props> = ({
  classId,
  className,
  year,
  sem,
  grade,
  no,
  catchErrorForModal,
  user,
}) => {
  // const [students, setStudents] = useState<StudentPHIAll[]>([]);
  // 同意書
  const [checkDate, setCheckDate] = useState<DateTime>();
  const [checkselfDate, setCheckSelfDate] = useState<DateTime>();
  const [checkagreefDate, setCheckAgreeDate] = useState<DateTime>();
  const checkHospitalRef = useRef<HTMLInputElement>(null);
  const checkHospitalTelRef = useRef<HTMLInputElement>(null);
  const checkHospitalTel0Ref = useRef<HTMLInputElement>(null);
  // 通知單
  const [checkpdfDate, setCheckPDFDate] = useState<DateTime>();

  const [radioAction, setRadioAction] = useState(CheckNoteSelType.AGREE);
  const [userinfo, setUser] = useState<UserInfo>();

  const previewPDF = (blob: Blob) => {
    const pdfBlob = new Blob([blob], {
      type: 'application/pdf',
    });

    const url = URL.createObjectURL(pdfBlob);

    const newWindow = window.open('', '_blank');

    if (!newWindow) {
      URL.revokeObjectURL(url);
      throw new Error('無法開啟 PDF 預覽視窗');
    }

    newWindow.location.href = url;

    // 不要在固定 60 秒後釋放 URL
    // 保留 PDF 的完整讀取能力
  };
  if (!classId) return <></>;
  return (
    <>
      <Row className="mb-2">
        <FormCheck
          className="mr-4"
          value={CheckNoteSelType.AGREE}
          type="radio"
          label={`同意書`}
          name="list-action"
          checked={radioAction === CheckNoteSelType.AGREE}
          onChange={() => setRadioAction(CheckNoteSelType.AGREE)}
        />
        <FormCheck
          className="mr-4"
          value={CheckNoteSelType.NOTICE}
          type="radio"
          label={`通知單`}
          name="list-action"
          checked={radioAction === CheckNoteSelType.NOTICE}
          onChange={() => setRadioAction(CheckNoteSelType.NOTICE)}
        />
        <FormCheck
          className="mr-auto"
          value={CheckNoteSelType.CHECK_LIST}
          type="radio"
          label={`檢查結果清單`}
          name="list-action"
          checked={radioAction === CheckNoteSelType.CHECK_LIST}
          onChange={() => setRadioAction(CheckNoteSelType.CHECK_LIST)}
        />
        <Button
          className="ml-auto mb-2"
          onClick={() => {
            if (radioAction === CheckNoteSelType.AGREE) {
              const isJunior = grade !== 10;
              toast
                .promise(
                  apis.downloadCheckAgreePDF(
                    classId,
                    [],
                    year,
                    sem,
                    isJunior,
                    checkHospitalRef.current?.value,
                    checkDate,
                    checkagreefDate,
                    checkselfDate,
                    checkHospitalTelRef.current?.value,
                    checkHospitalTel0Ref.current?.value
                  ),
                  {
                    pending: '下載中...',
                    success: '下載成功！',
                    error: '下載失敗！請查看錯誤資訊。',
                  }
                )
                .then((blob) => {
                  previewPDF(blob);
                })
                .catch(catchErrorForModal);
            } else if (radioAction === CheckNoteSelType.NOTICE) {
              toast
                .promise(
                  apis.downloadCheckNotePDF(
                    classId,
                    [],
                    year,
                    sem,
                    checkpdfDate
                  ),
                  {
                    pending: '下載中...',
                    success: '下載成功！',
                    error: '下載失敗！請查看錯誤資訊。',
                  }
                )
                .then((blob) => {
                  previewPDF(blob);
                })
                .catch(catchErrorForModal);
            } else if (radioAction === CheckNoteSelType.CHECK_LIST) {
              apis.getUserInfo()
                .then((u) => {
                  setUser(u);
                })
                .catch(catchErrorForModal);
              toast
                .promise(
                  apis.getClassStudentCheckNoteList(classId, year, sem),
                  {
                    pending: '下載中...',
                    success: '下載成功！',
                    error: '下載失敗！請查看錯誤資訊。',
                  }
                )
                .then((students) => {
                  const defaultStyle: CellStyle = {
                    alignment: { wrapText: true },
                    border: {
                      top: { color: { rgb: '121212' }, style: 'thin' },
                      bottom: { color: { rgb: '121212' }, style: 'thin' },
                      left: { color: { rgb: '121212' }, style: 'thin' },
                      right: { color: { rgb: '121212' }, style: 'thin' },
                    },
                  };
                  let maxNameLength = 0;
                  const results = students.map((student, i) => {
                    let state = '';
                    maxNameLength = Math.max(
                      maxNameLength,
                      student.name.length
                    );
                    if (!student.physical)
                      state = '--------------未受檢-----------';

                    const physicalSections = physicalGroupedKeys
                      .map((group, i) =>
                        generatePhysicalGroupedSection(
                          [...group],
                          student.physical
                        )
                      )
                      .join('');
                    const urineSection = generateUrineSection(
                      student.urine,
                      student.parasite
                    );
                    const bloodSection = generateBloodSection(student.blood);
                    const xraySection = generateXraySection(student.xray);
                    const result =
                      physicalSections +
                      urineSection +
                      bloodSection +
                      xraySection;
                    if (!state) {
                      if (result.length == 0)
                        state = '--------------無異狀-----------';
                      else if (result.length > 0) state = result;
                    }
                    const backgroundColor = i % 2 ? 'FFFFFF' : 'F7F7DE';
                    const style = {
                      ...defaultStyle,
                      fill: { fgColor: { rgb: backgroundColor } },
                    };
                    return {
                      cells: [
                        {
                          value: I18N.Grades[grade],
                          style,
                        },
                        { value: no, style },
                        { value: student.name, style },
                        { value: student.seat ?? 0, style },
                        { value: student.sex === '1' ? '男' : '女', style },
                        { value: state, style },
                      ],
                    };
                    // <tr
                    //   style={{ backgroundColor: i % 2 ? 'white' : '#F7F7DE' }}
                    //   key={i}
                    // >
                    //   <td>{I18N.Grades[grade]}</td>
                    //   <td>{no}</td>
                    //   <td>{student.name}</td>
                    //   <td>{student.seat}</td>
                    //   <td>{student.sex === '1' ? '男' : '女'}</td>
                    //   <td style={{ width: '800px' }}>{state}</td>
                    // </tr>
                  });
                  const title =
                    I18N.Grades[grade] +
                    '(' +
                    year +
                    ')' +
                    no +
                    '班健康檢查結果查詢統計表';

                  const headerStyle: CellStyle = {
                    ...defaultStyle,
                    fill: { fgColor: { rgb: '6B696B' } },
                    font: { color: { rgb: 'FFFFFF' }, bold: true },
                    alignment: {
                      horizontal: 'center',
                      vertical: 'center',
                    },
                  };
                  const sheetContent = [
                    {
                      cells: [
                        {
                          value: title,
                          merge: { column: 6 },
                          alignment: {
                            horizontal: 'center',
                            vertical: 'center',
                          },
                        },
                      ],
                    },
                    {
                      cells: [
                        { value: '年', style: headerStyle },
                        { value: '班', style: headerStyle },
                        { value: '姓名', style: headerStyle },
                        { value: '座號', style: headerStyle },
                        { value: '性別', style: headerStyle },
                        { value: '健檢結果', style: headerStyle },
                      ],
                    },
                    ...results,
                    { cells: [] },
                    {
                      cells: [
                        {
                          value:
                            '承辦人:　　　　組長:　　　　　主任:　　　　　　　校長:',
                        },
                      ],
                    },
                  ];
                  console.log(maxNameLength);
                  const sheet = generateSheet(sheetContent, {}, [
                    { wch: 6 },
                    { wch: 4 },
                    { wch: maxNameLength * 3 },
                    { wch: 6 },
                    { wch: 6 },
                    { wpx: 800 },
                  ]);
                  console.log(sheet);
                  downloadSheets(title, userinfo?.exportFileType, { name: title, sheet });
                })
                .catch(catchErrorForModal);
            }
          }}
        >
          <span className="feather icon-download"></span>
        </Button>
      </Row>

      {radioAction === CheckNoteSelType.AGREE && (
        <>
          <Row className="mb-2">
            <span className="rwid-900 mr-2">健檢日期</span>
            <DateTimePicker
              className="rwid-1400"
              value={checkDate}
              onChange={(date) => setCheckDate(date)}
            />
          </Row>
          <Row className="mb-2">
            <span className="rwid-900 mr-2">自行檢查繳回期限</span>
            <DateTimePicker
              className="rwid-1400"
              value={checkselfDate}
              onChange={(date) => setCheckSelfDate(date)}
            />
          </Row>
          <Row className="mb-2">
            <span className="rwid-900 mr-2">同意書繳回期限</span>
            <DateTimePicker
              className="rwid-1400"
              value={checkagreefDate}
              onChange={(date) => setCheckAgreeDate(date)}
            />
          </Row>
          <Row className="mb-2">
            <span className="rwid-900 mr-2">健檢醫院</span>
            <FormControl
              type="string"
              className="rwid-1400"
              aria-label="studentInput"
              aria-describedby="basic-addon1"
              ref={checkHospitalRef}
            />
          </Row>
          <Row className="mb-2">
            <span className="rwid-900 mr-2">電話</span>
            <FormControl
              type="string"
              className="rwid-1400"
              aria-label="studentInput"
              aria-describedby="basic-addon1"
              ref={checkHospitalTelRef}
            />
            <span className="mx-2">轉</span>
            <FormControl
              type="string"
              className="rwid-800"
              aria-label="studentInput"
              aria-describedby="basic-addon1"
              ref={checkHospitalTel0Ref}
            />
          </Row>
        </>
      )}
      {radioAction === CheckNoteSelType.NOTICE && (
        <>
          <Row className="mb-2">日期格式：1999/01/01</Row>
          <Row className="mb-2">
            <span className="rwid-900 mr-2">繳回期限</span>
            <DateTimePicker
              className="rwid-1400"
              value={checkpdfDate}
              onChange={(date) => setCheckPDFDate(date)}
            />
          </Row>
        </>
      )}
      <Row className="mb-2">
        健檢同意書內有健檢日期及健檢單位，產生全身健檢同意書前，請做好健檢日期及健檢單位設定，並更新到學生資料
      </Row>
      <Row className="mb-2">統計表中齲齒經驗不列入異常人數</Row>
      <Row className="mb-2">檢查結果通知單由健檢醫院提供</Row>
    </>
  );
};

export const CheckNoteSel = connector(WrapClassBaseLayout(checkNoteSel));

function generatePhysicalGroupedSection(
  properties: (keyof PHIPhysical)[],
  student?: PHIPhysical | null
): string {
  if (!student) return '';
  const result = properties
    .map((p) => {
      switch (p) {
        case 'e99':
        case 'o99':
        case 'n99':
        case 'c99':
        case 'a99':
        case 'l99':
        case 'u99':
        case 'd99':
        case 't99': {
          if (typeof student[p] === 'number' && !isExamOk(student[p])) {
            const stateKey = `${p}State` as keyof PHIPhysical;
            return '其他.' + student[p] + (student[stateKey] ?? '');
          }
          return '';
        }
        case 'limit':
        case 'assist':
        case 'special':
          return student[p] ? I18N.Physical[p] : '';
        case 'someProblem':
          return student[p]
            ? I18N.Physical[p] + student.subjects.join(',')
            : '';
        case 'finished':
          return student[p]
            ? I18N.Physical[p] + student.finishSubjects.join(',')
            : '';
        case 'trace':
          return student[p]
            ? I18N.Physical[p] + student.traceItems.join(',')
            : '';
        case 'memos':
          return student.memos && student.memos.length > 0
            ? '其他' + student.memos
            : '';
        case 'strabismus':
          return !isExamOk(student.strabismus)
            ? I18N.Physical[p] +
            '[' +
            I18N.StrabismusResult[student.strabismusType] +
            ']'
            : '';
        case 'hearing':
        case 'eardrum':
        case 'earMisshapen':
        case 'preauricularFistula':
        case 'cerumen': {
          if (typeof student[p] === 'number' && !isExamOk(student[p])) {
            const stateKey = `${p}LR` as keyof PHIPhysical;
            return I18N.Physical[p] + student[p] + student[stateKey];
          }
          return;
        }
        case 'tSheet':
          return;
        default:
          return !isExamOk(
            (student[p] as ExaminedResultEnum) || ExaminedResultEnum.NoExamined
          )
            ? I18N.Physical[p] + '.' + student[p]
            : '';
      }
    })
    .join('');
  return result && result.length > 0 ? '[' + result + ']' : '';
}

function generateUrineSection(
  urine?: PHIUrine | null,
  parasite?: PHIParasite | null
): string {
  const urineResult =
    urine && !isExamOk(urine.urine) ? '尿液.' + urine.urine : '';
  const parasiteResult =
    parasite && !isExamOk(parasite.parasite) ? '蟯蟲.' + parasite.parasite : '';
  const result = urineResult + parasiteResult;
  return result && result.length > 0 ? '[' + result + ']' : '';
}

function generateBloodSection(data?: PHIBlood | null): string {
  if (!data) return '';
  const result = bloodKeys
    .map((key) => {
      if (!isExamOk(data[key])) {
        return I18N.Blood[key] + data[key];
      }
      return '';
    })
    .join('');
  return result && result.length > 0 ? '[' + result + ']' : '';
}

function generateXraySection(data?: PHIXRay | null): string {
  if (!data) return '';
  const result = xrayKeys
    .map((key) => {
      if (isExamOk(data[key])) return '';

      switch (key) {
        case 'x99':
          return '其他.' + data.x99 + data.x99State;
        case 'c03':
        case 'c02':
          return '(X光)' + I18N.XRay[key] + data[key];
        default:
          return I18N.XRay[key] + data[key];
      }
    })
    .join('');
  return result && result.length > 0 ? '[' + result + ']' : '';
}

function isExamOk(value: ExaminedResultEnum) {
  return (
    value === ExaminedResultEnum.NoProblem ||
    value === ExaminedResultEnum.NoExamined
  );
}

const physicalGroupedKeys = [
  [
    'strabismus',
    // 'strabismusType',
    'trichiasis',
    'nystagmus',
    'blepharoptosis',
    'colorBlind',
    'e99',
    // 'e99state',
  ],
  [
    'hearing',
    // 'hearingLR',
    'eardrum',
    // 'eardrumLR',
    'earMisshapen',
    // 'earMisshapenLR',
    'clp',
    'articulationDisorders',
    'preauricularFistula',
    // 'preauricularFistulaLR',
    'cerumen',
    // 'cerumenLR',
    'rhinitis',
    'allergicRhinitis',
    'tonsillitis',
    'o99',
    // 'o99state',
  ],
  [
    'torticollis',
    'mass',
    'goiter',
    'lymphadenectasis',
    'mass99',
    'n99',
    // 'n99State',
  ],
  [
    'thorax',
    'heartMurmur',
    'arrhythmia',
    'wheeze',
    'cardiopulmonary',
    'cardiopulmonary99',
    'c99',
    // 'c99State',
  ],
  [
    'abdomen',
    'hernia',
    'a99',
    // 'a99State'
  ],
  [
    'scoliosis',
    'polydactyly',
    'squatting',
    'dysarthrosis',
    'dysmelia',
    'dysmelia99',
    'edema',
    'l99',
    // 'l99State',
  ],
  [
    'cryptorchidism',
    'scrotalSwelling',
    'prepuce',
    'varicocele',
    'u99',
    // 'u99State',
  ],
  [
    'epidermophytosis',
    'wart',
    'purpura',
    'scabies',
    'eczema',
    'atopicDermatitis',
    'd99',
    // 'd99State',
  ],
  [
    't01',
    't04',
    't05',
    't08',
    't06',
    't07',
    't11',
    't12',
    't13',
    't15',
    't09',
    't16',
    't17',
    't03',
    't18',
    't02',
    't19',
    't99',
    'tSheet',
    // 'T99state',
  ],
  ['bpResult', 'pulseResult'],
  [
    'someProblem',
    // 'subjects',
    'finished',
    // 'finishSubjects',
    'trace',
    // 'traceItems',
    'limit',
    'assist',
    'special',
    'memos',
  ],
] as const;
const bloodKeys = [
  'hbResult',
  'wbcResult',
  'rbcResult',
  'plResult',
  'mcvResult',
  'htResult',
  'clResult',
  'crResult',
  'uaResult',
  'bunResult',
  'gotResult',
  'gptResult',
  'hbsaResult',
  'antiHBResult',
  'tgResult',
  'hdlResult',
  'ldlResult',
  'acResult',
  'pcResult',
  'hBeAgResult',
] as const;
const xrayKeys = [
  'xRay',
  'phthisis',
  'calcify',
  'c03',
  'water',
  'c02',
  'bigHeart',
  'brochus',
  'x99',
] as const;
